// Iter74 — Snapshot Import / Replay Viewer.
//
// Purely client-side viewer that lets the operator load a previously
// downloaded Iter69 scanner snapshot envelope (or an Iter71 Manual
// Toolbox Preview snapshot, which uses the same `iter69_v1` shape)
// and re-render the saved scanner result inline — without calling
// any backend, without re-running any scanner, without contacting a
// broker / account / journal endpoint, and without persisting
// anything.
//
// HARD CONSTRAINTS (asserted by Iter74 tests):
//  * Zero outbound HTTP traffic. No `import axios`. No `fetch(`.
//    No `XMLHttpRequest`. No `navigator.sendBeacon`.
//  * No `useEffect` — purely state-driven, no side-effects on mount.
//  * No `import { API } from "@/App"` — URL construction is
//    impossible.
//  * No reference to `/api/forge/`, `/forge/toolbox/`,
//    `/forge/candidates`, `/forge/journal`, `/scan/`, `/api/scan`,
//    `/broker/`, `/account/`, `/positions/`, `/balances/`,
//    `modules-status`, any module preview / scanner endpoint,
//    `place_order`, `submit_order`, `tradier`, `tastytrade`,
//    `get_account(`, `fetch_positions(`, `exercise_leaps(`,
//    `automate_assignment(`, `auto_exercise(`, `auto_assign(`.
//  * No ranking, no winner selection, no recommendation. Component
//    only re-renders what is already stored in the snapshot file.
//  * File upload uses `FileReader.readAsText` only — never `FormData`,
//    never an upload URL, never `XHR`. Files stay on the local
//    machine.
//  * `Copy Loaded Snapshot JSON` button uses ONLY
//    `navigator.clipboard.writeText` — no network primitive.

import React, { useRef, useState } from "react";
import {
  AlertTriangle, ChevronDown, ChevronUp, ShieldAlert,
  FileJson, Eraser, Upload, History, Copy,
} from "lucide-react";

import {
  STATUS_COLOR_MAP, STATUS_NEUTRAL_DEFAULT,
  MetricChip, ReasonCodeChips,
  ScannerWarnings, ScannerErrorMessage,
  ScannerSpreadMetrics,
} from "@/components/ScannerResultNormalization";

// ── Identity + safety field lists (same set the Iter70 diff helper
//    surfaces — shared vocabulary for the cockpit).

const IDENTITY_KEYS = [
  "snapshot_version", "created_at",
  "source_panel", "module_id",
  "scanner_endpoint", "preview_endpoint",
  "scanner_version", "scanned_at",
  "total_symbols",
];

const SAFETY_FLAGS = [
  "read_only", "execution_enabled",
  "journal_mutation_enabled", "broker_execution_enabled",
  "assignment_automation_enabled", "exercise_automation_enabled",
];

const COUNT_ORDER = [
  // Spread / range modules.
  "enter", "watch",
  // Inventory modules.
  "buyback", "roll_out_up",
  // CFM-Shares specific.
  "allow_assignment", "defend",
  // CFM-LEAPS specific.
  "close_diagonal", "defend_leaps", "assignment_risk",
  // Common terminals.
  "blocked", "no_trade", "error",
];

// Per-module bespoke metric maps — read-only mirror of what the
// scanner panels render. We never *compute* any of these values;
// the snapshot already contains them.
const SHARES_METRICS = [
  { key: "share_adjusted_basis",     label: "Basis adj"   },
  { key: "assignment_exit_price",    label: "Exit @"      },
  { key: "assignment_pnl_per_share", label: "P&L/sh"      },
  { key: "premium_cushion_pct",      label: "Cushion %"   },
  { key: "extrinsic_value",          label: "Extrinsic"   },
];

const LEAPS_METRICS = [
  { key: "leaps_intrinsic",            label: "LEAPS intrinsic" },
  { key: "leaps_extrinsic",            label: "LEAPS extrinsic" },
  { key: "short_call_intrinsic",       label: "Short intrinsic" },
  { key: "short_call_extrinsic",       label: "Short extrinsic" },
  { key: "adjusted_leaps_basis",       label: "Adj basis"        },
  { key: "leaps_standalone_breakeven", label: "Standalone BE"    },
  { key: "current_campaign_value",     label: "Campaign value"   },
  { key: "campaign_pnl",               label: "Campaign P&L"     },
  { key: "net_delta",                  label: "Net delta"        },
  { key: "theta_carry_ratio",          label: "Theta carry"      },
];

// Which shared `<ScannerSpreadMetrics module=... />` to render for
// the result row based on module_id.
function spreadModuleForModuleId(mid) {
  if (mid === "BPCS" || mid === "BCCS" || mid === "IRON_CONDOR") {
    return mid;
  }
  return null;
}

// Try to find the response_result inside the snapshot. Iter69 + Iter71
// both store it under `response_result`. We also accept a bare scanner
// result (no envelope) for forward-compatibility with raw exports —
// in that case the snapshot_version is missing and the viewer reports
// it as an error per spec.
function extractResult(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return null;
  if (snapshot.response_result &&
      typeof snapshot.response_result === "object") {
    return snapshot.response_result;
  }
  return null;
}

// Validate the loaded snapshot. Returns either `{ ok: true, snapshot }`
// or `{ ok: false, error }`. Snapshot must be the Iter69 envelope
// shape (snapshot_version + response_result + module_id present).
function validateSnapshot(parsed) {
  if (parsed === null || typeof parsed !== "object" ||
      Array.isArray(parsed)) {
    return { ok: false,
              error: "Snapshot must be a JSON object." };
  }
  if (!parsed.snapshot_version) {
    return { ok: false,
              error: "Missing snapshot_version — not an ARXMENTIS scanner snapshot." };
  }
  if (!parsed.response_result ||
      typeof parsed.response_result !== "object") {
    return { ok: false,
              error: "Missing response_result — snapshot has no saved scanner result to replay." };
  }
  const mid = parsed.module_id ||
                parsed.response_result.module_id;
  if (!mid || typeof mid !== "string") {
    return { ok: false,
              error: "Missing module_id — cannot identify which scanner produced this snapshot." };
  }
  return { ok: true, snapshot: parsed };
}

// ── Tile ────────────────────────────────────────────────────────────

export default function SnapshotReplayViewer() {
  const [open, setOpen]             = useState(false);
  const [textValue, setTextValue]   = useState("");
  const [parseError, setParseError] = useState(null);
  const [loaded, setLoaded]         = useState(null);
  const [copyState, setCopyState]   = useState("idle");
  const fileInputRef = useRef(null);

  function handleLoad() {
    setCopyState("idle");
    if (!textValue.trim()) {
      setParseError("Paste or upload a snapshot JSON first.");
      setLoaded(null);
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(textValue);
    } catch (e) {
      setParseError(`Invalid JSON: ${e.message}`);
      setLoaded(null);
      return;
    }
    const v = validateSnapshot(parsed);
    if (!v.ok) {
      setParseError(v.error);
      setLoaded(null);
      return;
    }
    setParseError(null);
    setLoaded(v.snapshot);
  }

  function handleClear() {
    setTextValue("");
    setParseError(null);
    setLoaded(null);
    setCopyState("idle");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleFile(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setTextValue(String(ev.target?.result || ""));
    };
    reader.readAsText(f);
  }

  function handleCopy() {
    if (!loaded) return;
    const text = JSON.stringify(loaded, null, 2);
    try {
      navigator.clipboard.writeText(text).then(
        () => setCopyState("copied"),
        () => setCopyState("failed"),
      );
    } catch (_e) {
      setCopyState("failed");
    }
  }

  return (
    <div
      data-testid="snapshot-replay-viewer"
      className="rounded-sm border border-[#26262B] bg-[#0A0A0E] p-3"
    >
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="snapshot-replay-toggle"
        className="w-full flex items-center justify-between text-left"
      >
        <span className="flex items-center gap-2 text-[#FFB800]">
          <History size={14} />
          <span className="text-[12px] font-mono uppercase tracking-[0.18em]">
            Snapshot Import / Replay Viewer
          </span>
          <span className="text-[9px] text-[#5F5F66] uppercase tracking-[0.18em]">
            · Iter74 · client-side · no scanner re-run
          </span>
        </span>
        {open
          ? <ChevronUp size={14} className="text-[#8A8A93]" />
          : <ChevronDown size={14} className="text-[#8A8A93]" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {/* Safety banner */}
          <div
            data-testid="snapshot-replay-banner"
            className="flex flex-wrap items-center gap-2 text-[9px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]"
          >
            <ShieldAlert size={11} className="text-[#FFB800]" />
            <span data-testid="snapshot-replay-banner-client-side">
              Client-side snapshot viewer only.
            </span>
            <span data-testid="snapshot-replay-banner-no-backend">
              · No backend calls.
            </span>
            <span data-testid="snapshot-replay-banner-no-rerun">
              · No scanner re-run.
            </span>
            <span data-testid="snapshot-replay-banner-no-execution">
              · No trade execution.
            </span>
            <span data-testid="snapshot-replay-banner-no-journal">
              · No journal write.
            </span>
            <span data-testid="snapshot-replay-banner-no-broker">
              · No broker action.
            </span>
          </div>

          {/* Input row: textarea + file upload */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <label
                className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] flex items-center gap-1"
              >
                <FileJson size={11} />
                Snapshot JSON
              </label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFile}
                  data-testid="snapshot-replay-file-input"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="snapshot-replay-upload"
                  className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
                >
                  <Upload size={11} />
                  Upload .json
                </button>
              </div>
            </div>
            <textarea
              data-testid="snapshot-replay-textarea"
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              spellCheck={false}
              placeholder='{"snapshot_version":"iter69_v1", ...}'
              className="w-full h-32 text-[10px] font-mono bg-[#08080A] border border-[#26262B] rounded-sm p-2 text-[#E0E0E0] focus:outline-none focus:border-[#FFB800]/40"
            />
          </div>

          {/* Action bar */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleLoad}
              data-testid="snapshot-replay-load"
              className="px-3 py-1 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#FFB800] hover:bg-[#FFB800]/20 inline-flex items-center gap-1"
            >
              <FileJson size={11} />
              Load Snapshot
            </button>
            <button
              type="button"
              onClick={handleClear}
              data-testid="snapshot-replay-clear"
              className="px-3 py-1 rounded-sm border border-[#26262B] text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
            >
              <Eraser size={11} />
              Clear / Reset
            </button>
            {loaded && (
              <button
                type="button"
                onClick={handleCopy}
                data-testid={
                  copyState === "copied"
                    ? "snapshot-replay-copy-success"
                    : copyState === "failed"
                      ? "snapshot-replay-copy-failed"
                      : "snapshot-replay-copy"
                }
                className="px-3 py-1 rounded-sm border border-[#26262B] text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
              >
                <Copy size={11} />
                {copyState === "copied"
                  ? "✓ Copied"
                  : copyState === "failed"
                    ? "× Copy failed"
                    : "Copy Loaded Snapshot JSON"}
              </button>
            )}
          </div>

          {/* Error block */}
          {parseError && (
            <div
              data-testid="snapshot-replay-error"
              className="text-[10px] font-mono text-[#FF3366] flex items-start gap-1"
            >
              <AlertTriangle size={11} className="mt-0.5 shrink-0" />
              <span>{parseError}</span>
            </div>
          )}

          {/* Loaded snapshot rendering */}
          {loaded && (
            <ReplayedSnapshot snapshot={loaded} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Replayed snapshot renderer ─────────────────────────────────────

function ReplayedSnapshot({ snapshot }) {
  const result = extractResult(snapshot) || {};
  const moduleId = snapshot.module_id || result.module_id || "";
  const spreadMod = spreadModuleForModuleId(moduleId);
  const counts = result.counts || {};
  const rows = Array.isArray(result.results) ? result.results : [];

  return (
    <div
      data-testid="snapshot-replay-output"
      className="rounded-sm border border-[#26262B] bg-[#08080A] p-3 space-y-3"
    >
      {/* Identity */}
      <section data-testid="snapshot-replay-identity">
        <h3 className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FFB800] mb-1">
          Snapshot identity
        </h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] font-mono">
          {IDENTITY_KEYS.map(k => {
            const v = snapshot[k] !== undefined
              ? snapshot[k]
              : (result[k] !== undefined ? result[k] : null);
            if (v === null || v === undefined || v === "") return null;
            return (
              <div
                key={k}
                data-testid={`snapshot-replay-identity-${k}`}
                className="flex items-center gap-2"
              >
                <span className="text-[#5F5F66] uppercase tracking-[0.12em]">
                  {k}
                </span>
                <span className="text-[#E0E0E0] truncate">
                  {String(v)}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Safety flags */}
      <section data-testid="snapshot-replay-safety">
        <h3 className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FFB800] mb-1">
          Safety / disclosure flags
        </h3>
        <div className="flex flex-wrap gap-2 text-[9px] font-mono">
          {SAFETY_FLAGS.map(k => {
            const sv = snapshot[k] !== undefined
              ? snapshot[k]
              : (result[k] !== undefined ? result[k] : undefined);
            if (sv === undefined) return null;
            const safe = sv === true || sv === "true";
            const isReadOnly = k === "read_only";
            // read_only=true is the "safe" green case; everything
            // else that says true would be a red flag, so colour
            // accordingly.
            const positive = isReadOnly ? safe : !safe;
            return (
              <span
                key={k}
                data-testid={`snapshot-replay-safety-${k}`}
                className={`px-1.5 py-0.5 rounded-sm border ${
                  positive
                    ? "border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88]"
                    : "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]"
                }`}
              >
                {k}: {String(sv)}
              </span>
            );
          })}
        </div>
      </section>

      {/* Request payload (collapsed) */}
      {snapshot.request_payload_used !== undefined && (
        <details
          data-testid="snapshot-replay-request-payload"
          className="border border-[#26262B] rounded-sm"
        >
          <summary className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
            Request payload used (saved)
          </summary>
          <pre className="m-0 px-2 py-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] max-h-64 overflow-auto">
            {JSON.stringify(snapshot.request_payload_used, null, 2)}
          </pre>
        </details>
      )}

      {/* Replayed result */}
      <section data-testid="snapshot-replay-result">
        <h3 className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FFB800] mb-1">
          Replayed scanner result
        </h3>
        <div className="flex flex-wrap items-center gap-3 mb-2 text-[10px] font-mono">
          {result.scanner_version && (
            <span
              data-testid="snapshot-replay-result-scanner-version"
              className="text-[#8A8A93]"
            >
              <span className="uppercase tracking-[0.12em]">scanner</span>{" "}
              <span className="text-[#E0E0E0]">{result.scanner_version}</span>
            </span>
          )}
          {moduleId && (
            <span
              data-testid="snapshot-replay-result-module-id"
              className="text-[#8A8A93]"
            >
              <span className="uppercase tracking-[0.12em]">module</span>{" "}
              <span className="text-[#E0E0E0]">{moduleId}</span>
            </span>
          )}
          {result.total_symbols !== undefined && (
            <span
              data-testid="snapshot-replay-result-total-symbols"
              className="text-[#8A8A93]"
            >
              <span className="uppercase tracking-[0.12em]">total</span>{" "}
              <span className="text-[#E0E0E0]">{result.total_symbols}</span>
            </span>
          )}
        </div>

        {/* Counts */}
        {result.counts && typeof result.counts === "object" && (
          <div
            data-testid="snapshot-replay-result-counts"
            className="flex flex-wrap gap-3 text-[10px] font-mono mb-2"
          >
            {COUNT_ORDER.filter(k => counts[k] !== undefined).map(k => (
              <span
                key={k}
                data-testid={`snapshot-replay-result-count-${k}`}
                className="flex items-center gap-1 text-[#8A8A93]"
              >
                <span className="uppercase tracking-[0.12em]">{k}</span>
                <span className="font-bold text-[#E0E0E0]">{counts[k] ?? 0}</span>
              </span>
            ))}
            {/* surface any non-canonical buckets too */}
            {Object.keys(counts)
              .filter(k => !COUNT_ORDER.includes(k))
              .map(k => (
                <span
                  key={k}
                  data-testid={`snapshot-replay-result-count-${k}`}
                  className="flex items-center gap-1 text-[#8A8A93]"
                >
                  <span className="uppercase tracking-[0.12em]">{k}</span>
                  <span className="font-bold text-[#E0E0E0]">{counts[k] ?? 0}</span>
                </span>
              ))}
          </div>
        )}

        {/* Top-level warnings */}
        {Array.isArray(result.warnings) && result.warnings.length > 0 && (
          <ScannerWarnings
            warnings={result.warnings}
            testid="snapshot-replay-result-warnings"
          />
        )}

        {/* Per-symbol rows */}
        <ul className="space-y-2 mt-2">
          {rows.map(rec => (
            <li
              key={rec.symbol || JSON.stringify(rec).slice(0, 16)}
              data-testid={`snapshot-replay-result-row-${rec.symbol}`}
              className="rounded-sm border border-[#26262B] bg-[#0A0A0E] p-2"
            >
              <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
                <span
                  data-testid={`snapshot-replay-result-row-symbol-${rec.symbol}`}
                  className="text-[#E0E0E0] font-bold uppercase tracking-[0.14em]"
                >
                  {rec.symbol}
                </span>
                <span
                  data-testid={`snapshot-replay-result-row-status-${rec.symbol}`}
                  className={`px-2 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.14em] ${
                    STATUS_COLOR_MAP[rec.status] || STATUS_NEUTRAL_DEFAULT
                  }`}
                >
                  {rec.status}
                </span>
                {rec.decision && (
                  <span
                    data-testid={`snapshot-replay-result-row-decision-${rec.symbol}`}
                    className="text-[#8A8A93]"
                  >
                    {rec.decision}
                  </span>
                )}
                {rec.confidence_score !== undefined && (
                  <span
                    data-testid={`snapshot-replay-result-row-conf-${rec.symbol}`}
                    className="text-[#E0E0E0]"
                  >
                    CONF {rec.confidence_score ?? 0}
                  </span>
                )}
                {rec.risk_score !== undefined && (
                  <span
                    data-testid={`snapshot-replay-result-row-risk-${rec.symbol}`}
                    className="text-[#FFB800]"
                  >
                    RISK {rec.risk_score ?? 0}
                  </span>
                )}
                {rec.regime && (
                  <span
                    data-testid={`snapshot-replay-result-row-regime-${rec.symbol}`}
                    className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/30 bg-[#FFB800]/5 text-[9px] uppercase tracking-[0.12em] text-[#FFB800]"
                  >
                    {rec.regime}
                  </span>
                )}
                {Array.isArray(rec.reason_codes) && rec.reason_codes.length > 0 && (
                  <ReasonCodeChips
                    codes={rec.reason_codes}
                    testid={`snapshot-replay-result-row-reasons-${rec.symbol}`}
                  />
                )}
              </div>

              {/* Spread metrics (BPCS / BCCS / Iron Condor) */}
              {spreadMod && (
                <ScannerSpreadMetrics
                  rec={rec}
                  module={spreadMod}
                  testidPrefix="snapshot-replay-result-row"
                />
              )}

              {/* CFM-Shares bespoke metrics */}
              {moduleId === "CFM_CC_SHARES" && (
                <div
                  data-testid={`snapshot-replay-result-row-shares-metrics-${rec.symbol}`}
                  className="mt-1 flex flex-wrap gap-x-3 gap-y-1"
                >
                  {SHARES_METRICS.map(m => (
                    <MetricChip
                      key={m.key}
                      label={m.label}
                      value={rec[m.key]}
                      testid={`snapshot-replay-result-row-${m.key}-${rec.symbol}`}
                    />
                  ))}
                </div>
              )}

              {/* CFM-LEAPS bespoke metrics */}
              {moduleId === "CFM_CC_LEAPS" && (
                <div
                  data-testid={`snapshot-replay-result-row-leaps-metrics-${rec.symbol}`}
                  className="mt-1 flex flex-wrap gap-x-3 gap-y-1"
                >
                  {LEAPS_METRICS.map(m => (
                    <MetricChip
                      key={m.key}
                      label={m.label}
                      value={rec[m.key]}
                      testid={`snapshot-replay-result-row-${m.key}-${rec.symbol}`}
                    />
                  ))}
                </div>
              )}

              {/* Per-row warnings */}
              {Array.isArray(rec.warnings) && rec.warnings.length > 0 && (
                <ScannerWarnings
                  warnings={rec.warnings}
                  testid={`snapshot-replay-result-row-warnings-${rec.symbol}`}
                />
              )}

              {/* Per-row error */}
              {rec.error_message && (
                <ScannerErrorMessage
                  message={rec.error_message}
                  testid={`snapshot-replay-result-row-error-${rec.symbol}`}
                />
              )}

              {/* Plain English / summary */}
              {(rec.plain_english || rec.summary) && (
                <div
                  data-testid={`snapshot-replay-result-row-plain-${rec.symbol}`}
                  className="mt-1 text-[10px] font-mono text-[#E0E0E0] leading-relaxed"
                >
                  {rec.plain_english || rec.summary}
                </div>
              )}

              {/* Selected structure (collapsed) */}
              {(rec.selected_structure || rec.option_plan) && (
                <details
                  data-testid={`snapshot-replay-result-row-structure-${rec.symbol}`}
                  className="mt-1"
                >
                  <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
                    Selected structure / option plan
                  </summary>
                  <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 rounded-sm border border-[#26262B] max-h-64 overflow-auto">
                    {JSON.stringify(
                      rec.selected_structure || rec.option_plan, null, 2
                    )}
                  </pre>
                </details>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* Raw JSON (collapsed) */}
      <details
        data-testid="snapshot-replay-raw"
        className="border border-[#26262B] rounded-sm"
      >
        <summary className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
          Raw snapshot JSON
        </summary>
        <pre className="m-0 px-2 py-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] max-h-64 overflow-auto">
          {JSON.stringify(snapshot, null, 2)}
        </pre>
      </details>

      {/* Footer disclaimer */}
      <div
        data-testid="snapshot-replay-disclaimer"
        className="text-[9px] font-mono text-[#5F5F66] uppercase tracking-[0.12em]"
      >
        Local replay of saved snapshot. No backend was contacted, no
        scanner was re-run, no order was placed, no journal record
        was created.
      </div>
    </div>
  );
}
